import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding, imgStyle } from "../context/BrandingContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

export function googleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton() {
  return (
    <button type="button" data-testid="google-signin-button" onClick={googleSignIn}
      className="w-full h-11 rounded-md border border-neutral-600 bg-white text-black font-semibold text-sm flex items-center justify-center gap-2 hover:bg-neutral-100 transition">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
      </svg>
      Continue with Google
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const crest = branding.images.login_crest;
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, brother.");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-grain flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={crest.src} alt="Chapter crest"
               style={{ ...imgStyle(crest, 96), marginBottom: "1rem" }}
               className="h-auto drop-shadow-[0_0_24px_rgba(255,236,105,0.25)]" />
          <h1 className="font-serif-accent text-3xl sm:text-4xl text-[var(--sn-gold)] mb-1 text-center">Sigma Nu · Nu Epsilon</h1>
          <p className="text-[var(--sn-border)] text-xs tracking-[0.25em] uppercase font-semibold">Excelling With Honor</p>
        </div>
        <form onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8 space-y-5">
          <div>
            <h2 className="font-heading text-2xl mb-1">Sign In</h2>
            <p className="text-neutral-400 text-sm">Access your chapter&apos;s social calendar.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input data-testid="login-email-input" id="email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="brother@chapter.edu"
              className="bg-neutral-900 border-[var(--sn-border)] focus-visible:ring-[var(--sn-gold)]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input data-testid="login-password-input" id="password" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="bg-neutral-900 border-[var(--sn-border)] focus-visible:ring-[var(--sn-gold)]" />
          </div>
          <Button data-testid="login-submit-button" type="submit" disabled={loading}
            className="w-full bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold h-11">
            {loading ? "Signing in…" : "Enter Chapter"}
          </Button>
          <div className="flex items-center gap-3 text-neutral-600 text-xs">
            <div className="flex-1 h-px bg-neutral-800" /> or <div className="flex-1 h-px bg-neutral-800" />
          </div>
          <GoogleButton />
          <p className="text-neutral-500 text-sm text-center">
            No account yet?{" "}
            <Link to="/register" className="text-[var(--sn-gold)] hover:brightness-90 underline">Request access</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
